import { useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  getPendingList,
  getApprovedList,
  getPendingListById,
} from "../../../store/slices/Verification/verificationOldCardReissueSlice";
import { toast } from "react-toastify";
import { approveStockData, rejectStockData } from "./api";
import { updateConfig } from "../../../store/slices/Verification/verificationOldCardReissueSlice";
import getFilterCount from "../../../utils/getFilterCount";

const useVerificationReassign = () => {
  const data = [{ title: "Pending Queue" }, { title: "Approved" }];
  const state = useSelector((state) => state.verificationOldCardReissue);
  const [pendingPage, setPendingPage] = useState(1);
  const [approvedPage, setApprovedPage] = useState(1);
  const [showDetail, setShowDetail] = useState(false);
  const [showApproved, setShowApproved] = useState(false);
  const [filterShow, setFilterShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [gliNumber, setGliNumber] = useState("");
  const [activeCard, setActiveCard] = useState({ id: "", key: "" });
  const [selectedApprovedItem, setSelectedApprovedItem] = useState(null);
  const [certModalShow, setCertModalShow] = useState(false);
  const [certInput, setCertInput] = useState("");
  const [enteredCertificates, setEnteredCertificates] = useState([]);
  const [requiredCertCount, setRequiredCertCount] = useState(0);
  const states = state?.verificationData;
  const dispatch = useDispatch();

  const handleOpenCertModal = () => {
    if (!requiredCertCount) {
      toast.error("No certificate count available for this stock");
      return;
    }
    setCertModalShow(true);
  };

  const handleCloseCertModal = () => {
    setCertModalShow(false);
    setCertInput("");
    setEnteredCertificates([]);
  };

  const handleAddCertificate = () => {
    const value = certInput?.trim();
    if (!value) {
      toast.error("Please enter a certificate number");
      return;
    }
    if (enteredCertificates.includes(value)) {
      toast.error("Certificate number already added");
      return;
    }
    if (enteredCertificates.length >= requiredCertCount) {
      toast.error(`Only ${requiredCertCount} certificate numbers allowed`);
      return;
    }
    setEnteredCertificates((prev) => [...prev, value]);
    setCertInput("");
  };

  const handleRemoveCertificate = (index) => {
    setEnteredCertificates((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmitCertificates = () => {
    if (enteredCertificates.length !== requiredCertCount) {
      toast.error(
        `Please enter exactly ${requiredCertCount} certificate numbers`
      );
      return;
    }
    approveStockData(state?.pendingData?._id, enteredCertificates).then(
      (response) => {
        if (response?.data?.status_code === 200) {
          toast.success("Stock verified successfully");
          handleCloseCertModal();
          dispatch(
            updateConfig((state) => {
              state.verificationData.Pending = [];
              state.verificationData.Approved = [];
            })
          );
          dispatch(getPendingList({ page: 1, per_page: 10 }));
          dispatch(getApprovedList({ page: 1, per_page: 10 }));
          setShowDetail(false);
        } else {
          toast.error(response?.message);
        }
      }
    );
  };

  const handleLoadMore = (mouseHover) => {
    if (
      mouseHover === "Pending" &&
      states?.Pending?.length !== state?.PendingTotal
    ) {
      setPendingPage(pendingPage + 1);
    }
    if (
      mouseHover === "Approved" &&
      states?.Approved?.length !== state?.ApprovedTotal
    ) {
      setApprovedPage(approvedPage + 1);
    }
  };

  useEffect(() => {
    dispatch(
      getPendingList({
        page: pendingPage,
        per_page: 10,
      })
    );
    // eslint-disable-next-line
  }, [pendingPage]);

  useEffect(() => {
    dispatch(
      getApprovedList({
        page: approvedPage,
        per_page: 10,
      })
    );
    // eslint-disable-next-line
  }, [approvedPage]);

  useEffect(() => {
    if (isLoading) {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.pendingStatus, state?.approvedStatus]);

  const handleSearchInput = (e) => {
    const searchValue = e.target.value;
    dispatch(updateConfig((state) => (state.search = searchValue)));
  };

  // Inside your component
  const handleSearch = () => {
    const searchValue = state?.search;

    dispatch(
      updateConfig((state) => {
        state.verificationData.Pending = [];
        state.verificationData.Approved = [];
      })
    );
    dispatch(
      getPendingList({
        search: searchValue,
        page: 1,
      })
    );
    dispatch(
      getApprovedList({
        search: searchValue,
        page: 1,
      })
    );
  };

  const handleChange = (item, key) => {
    setGliNumber(item?.gli_number);
    setActiveCard({ id: item?._id, key: key });
    if (key === "Pending") {
      setShowDetail(true);
      setShowApproved(false);
      setSelectedApprovedItem(null);
      setRequiredCertCount(Number(item?.certificate_count) || 0);
      setEnteredCertificates([]);
      setCertInput("");
      dispatch(getPendingListById(item?._id));
    } else {
      setShowApproved(true);
      setShowDetail(false);
      setSelectedApprovedItem(item);
    }
  };

  const handleReject = () => {
    dispatch(updateConfig((state) => (state.rejectModal = !state.rejectModal)));
  };

  const getFieldError = () => {};

  const handleRejectRequest = () => {
    handleReject();
    rejectStockData(state?.pendingData?._id).then((response) => {
      if (response?.data?.status_code === 200) {
        toast.success("Stock rejected successfully");
        dispatch(
          updateConfig((state) => {
            state.verificationData.Pending = [];
          })
        );
        dispatch(
          getPendingList({
            page: 1,
            per_page: 10,
          })
        );
        setShowDetail(false);
      } else {
        toast.error(response?.message);
        setShowDetail(false);
      }
    });
  };

  const filterCount = useMemo(() => {
    return getFilterCount({
      from_date: state.from_date,
      to_date: state.to_date,
      item_type: state.item_type,
      shop: state.shop,
      status: state.status,
    });
  }, [
    state.from_date,
    state.to_date,
    state.item_type,
    state.shop,
    state.status,
  ]);

  return {
    data,
    states,
    state,
    gliNumber,
    showDetail,
    showApproved,
    selectedApprovedItem,
    activeCard,
    filterShow,
    filterCount,
    isLoading,
    certModalShow,
    certInput,
    setCertInput,
    enteredCertificates,
    requiredCertCount,
    totalLength: {
      Pending: state?.PendingTotal,
      Approved: state?.ApprovedTotal,
    },
    apiStatus: {
      Pending: state?.pendingStatus,
      Approved: state?.approvedStatus,
    },
    handleLoadMore,
    setIsLoading,
    handleReject,
    getFieldError,
    handleSearchInput,
    handleSearch,
    setFilterShow,
    handleChange,
    setShowDetail,
    handleRejectRequest,
    handleOpenCertModal,
    handleCloseCertModal,
    handleAddCertificate,
    handleRemoveCertificate,
    handleSubmitCertificates,
  };
};

export default useVerificationReassign;
